/**
 * Global "this feature requires upgrading" modal.
 *
 * Mounted once at the App root. Listens to `useUpgradeGateStore` and
 * appears whenever something — either the API interceptor catching a
 * 4xx with `{ feature, requiredPlan }`, or a component calling
 * `openUpgradeGate()` imperatively — pushes a context.
 *
 * Design choices
 * --------------
 *
 *   • One modal, one purpose: convert the gate hit into either an
 *     upgrade click or a clean dismiss. NO secondary "tell me more"
 *     content here — for full feature comparison the user clicks
 *     "Compare all plans" → /upgrade.
 *
 *   • Renders even when off-route (the store is global). We use a
 *     `<Link>` from react-router for the upgrade CTA so it works
 *     inside the BrowserRouter boundary.
 *
 *   • Lock + sparkle motif: visually distinct from the session-expired
 *     toast and the generic error states so users instantly read it as
 *     "paywall, not error". Gradient buttons match the rest of the
 *     pricing UI for continuity.
 *
 *   • ESC + click-outside both close. The modal is a soft block — the
 *     user's previous request already failed; we never trap them.
 *
 *   • i18n via the `billing` namespace which already loads on every
 *     authenticated screen.
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Sparkles, X, ArrowRight, Check } from 'lucide-react';
import { useUpgradeGateStore } from '../../store/upgradeGateStore';
import {
  PLANS,
  formatUsd,
  effectiveMonthlyUsd,
  type Tier,
  type PlanId,
} from '../../constants/pricing';

const TIER_GRADIENT: Record<Tier, string> = {
  free: 'from-gray-500 to-gray-600',
  pro: 'from-blue-500 to-indigo-600',
  agency: 'from-purple-500 to-pink-600',
  enterprise: 'from-amber-500 to-orange-600',
};

/**
 * Pick a representative price for the "From $X/mo" display. We choose
 * yearly / 12 because that's the genuinely lowest sticker the user will
 * see on /upgrade — better to over-deliver at checkout than under-show
 * here and look more expensive than the public pricing page.
 */
function priceForTierFrom(tier: Tier): string | null {
  if (tier === 'free') return null;
  const yearlyPlanId = `${tier}_yearly` as PlanId;
  if (PLANS[yearlyPlanId]) {
    return formatUsd(effectiveMonthlyUsd(yearlyPlanId));
  }
  return null;
}

export function UpgradeGateModal() {
  const { t } = useTranslation('billing');
  const { isOpen, context, close } = useUpgradeGateStore();

  // ESC closes the modal. Attached only when open so we don't pin a
  // listener that does nothing 99% of the time.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  // Lock body scroll while open. Mirrors common modal patterns; restores
  // the original overflow value so we don't fight other components.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  const featureLabel = useMemo(() => {
    if (!context) return '';
    const key = `upgradeGate.feature.${context.feature}`;
    const translated = t(key);
    // i18next returns the key itself when missing — fall back to the
    // raw feature key in that case so unknown gates still render
    // something readable instead of "upgradeGate.feature.foo".
    return translated === key ? context.feature : translated;
  }, [context, t]);

  if (!isOpen || !context) return null;

  const { requiredPlan, currentPlan, message, upgradeUrl } = context;
  const tierName = t(`upgradeGate.tier.${requiredPlan}`);
  const fromPrice = priceForTierFrom(requiredPlan);
  const cta = upgradeUrl || '/upgrade';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-150"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-gate-title"
    >
      {/* Scrim */}
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={close}
      />

      {/* Card */}
      <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-2xl ring-1 ring-black/10 dark:ring-white/10 overflow-hidden">
        {/* Close (X) */}
        <button
          type="button"
          onClick={close}
          aria-label={t('upgradeGate.actions.later') as string}
          className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <X size={18} />
        </button>

        {/* Header gradient strip */}
        <div className={`h-2 bg-gradient-to-r ${TIER_GRADIENT[requiredPlan]}`} />

        <div className="px-6 pt-7 pb-6">
          {/* Lock icon medallion */}
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500/15 via-indigo-500/15 to-purple-500/15 ring-1 ring-blue-500/20">
            <Lock className="h-7 w-7 text-blue-600 dark:text-blue-400" strokeWidth={2.25} />
          </div>

          <h2
            id="upgrade-gate-title"
            className="text-center text-xl font-bold text-gray-900 dark:text-white"
          >
            {t('upgradeGate.title')}
          </h2>

          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">
            {message ||
              (featureLabel
                ? t('upgradeGate.subtitleWithFeature', {
                    feature: featureLabel,
                    plan: tierName,
                  })
                : t('upgradeGate.subtitleGeneric', { plan: tierName }))}
          </p>

          {/* From → To pill */}
          <div className="mt-5 flex items-center justify-center gap-2 text-xs">
            <span className="rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
              {t(`upgradeGate.tier.${currentPlan}`)}
            </span>
            <ArrowRight className="h-3.5 w-3.5 text-gray-400" />
            <span
              className={`rounded-full bg-gradient-to-r ${TIER_GRADIENT[requiredPlan]} px-2.5 py-1 font-semibold uppercase tracking-wide text-white shadow-sm`}
            >
              {tierName}
            </span>
          </div>

          {fromPrice && (
            <div className="mt-4 flex items-center justify-center gap-1.5 text-sm">
              <Sparkles className="h-4 w-4 text-amber-500" />
              <span className="font-semibold text-gray-900 dark:text-white">
                {t('upgradeGate.priceFrom', { price: fromPrice })}
              </span>
            </div>
          )}

          {/* Reassurance bullets */}
          <ul className="mt-5 space-y-2 text-sm text-gray-600 dark:text-gray-300">
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
              <span>{t('upgradeGate.benefit.instant')}</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
              <span>{t('upgradeGate.benefit.noLimit')}</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
              <span>{t('upgradeGate.benefit.secure')}</span>
            </li>
          </ul>

          {/* CTAs */}
          <div className="mt-6 flex flex-col gap-2">
            <Link
              to={cta}
              onClick={close}
              className={`inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r ${TIER_GRADIENT[requiredPlan]} px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all`}
            >
              {t('upgradeGate.actions.upgrade', { plan: tierName })}
              <ArrowRight className="h-4 w-4" />
            </Link>

            <button
              type="button"
              onClick={close}
              className="inline-flex w-full items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {t('upgradeGate.actions.later')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UpgradeGateModal;
